import React from 'react';
import {useIntl} from 'react-intl';
import {Button, BUTTON_VARIANT, Text} from '@lookiero/aurora';
import {OrderApplication} from '@application/Order';
import {AuthApplication} from '@application/Auth';
import {eventManager} from '@application/Event';
import {UserEvent} from '@domain/model/Event';
import {useOverallInfo, useToast} from '@components/_context';
import {MountableModal} from '@components/molecules';

import {DATE_FORMAT, IDS, TEXT} from './ModalOrderCancel.definition';
import {style} from './ModalOrderCancel.styles';
import {useLoading} from '@views/Account/Plan/_hooks/useLoading';

export type ModalOrderCancelProps = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
  date: Date;
  orderId: string;
};

export const ModalOrderCancel: React.FC<ModalOrderCancelProps> = ({isVisible, onClose, onSuccess, date, orderId}) => {
  const {showSuccess, showError} = useToast();
  const {loadUserOverallInfo, hasAnyOrders} = useOverallInfo();
  const {formatDate, formatMessage} = useIntl();
  const {loading, startLoading, stopLoading} = useLoading();

  const handleCancelOrder = async (): Promise<boolean> => {
    try {
      await OrderApplication.cancelOrder();
      showSuccess({message: formatMessage(TEXT.TOAST_SUCCESS_CANCEL)});

      const {id} = await AuthApplication.getLoginInfo();
      eventManager.emit(UserEvent.ORDER_CANCELLED, {orderId, userId: id, isFirstOrder: !hasAnyOrders, promoCode: ''});
      return true;
    } catch {
      showError({message: formatMessage(TEXT.TOAST_ERROR)});
      return false;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      startLoading();

      const success = await handleCancelOrder();

      if (success) {
        await loadUserOverallInfo();
        await onSuccess();
        onClose();
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <MountableModal
      portalID="modal-return-instructions"
      isVisible={isVisible}
      onClose={onClose}
      title={formatMessage(TEXT.MODAL_ORDER_TITLE)}
      description={formatMessage(TEXT.MODAL_ORDER_CANCEL_DESCRIPTION, {
        date: (
          <Text level={3} action>
            {formatDate(date, DATE_FORMAT)}
          </Text>
        ),
      })}
    >
      <Button
        nativeID={IDS.CANCEL_ORDER_BUTTON}
        busy={loading}
        style={style.ctaButton}
        onPress={handleSubmit}
        testID="confirm_cancel-order-button"
      >
        {formatMessage(TEXT.MODAL_ORDER_CANCEL_CTA)}
      </Button>
      <Button onPress={onClose} variant={BUTTON_VARIANT.SECONDARY}>
        {formatMessage(TEXT.MODAL_ORDER_CANCEL_ACTION)}
      </Button>
    </MountableModal>
  );
};
